import { readFileSync } from "node:fs";
import { resolve, dirname, extname } from "node:path";
import { parse as parseYaml } from "yaml";

export class RefResolutionError extends Error {
  constructor(
    public readonly ref: string,
    message: string,
  ) {
    super(`Cannot resolve $ref "${ref}": ${message}`);
    this.name = "RefResolutionError";
  }
}

export interface ResolveRefsOptions {
  basePath?: string;
}

function loadFile(filePath: string, fileCache: Map<string, unknown>): unknown {
  const absPath = resolve(filePath);
  const cached = fileCache.get(absPath);
  if (cached !== undefined) {
    return cached;
  }

  try {
    const content = readFileSync(absPath, "utf-8");
    const ext = extname(absPath).toLowerCase();
    const parsed =
      ext === ".json" ? (JSON.parse(content) as unknown) : parseYaml(content);
    fileCache.set(absPath, parsed);
    return parsed;
  } catch (err) {
    throw new RefResolutionError(
      filePath,
      `Cannot read external file: ${(err as Error).message}`,
    );
  }
}

function resolveJsonPointer(root: unknown, pointer: string): unknown {
  const segments = pointer
    .split("/")
    .filter((s) => s.length > 0)
    .map((s) => s.replace(/~1/g, "/").replace(/~0/g, "~"));

  let current: unknown = root;
  for (const segment of segments) {
    if (current === null || current === undefined || typeof current !== "object") {
      throw new RefResolutionError(
        `#${pointer}`,
        `Cannot traverse into non-object at "${segment}"`,
      );
    }
    const obj = current as Record<string, unknown>;
    if (!(segment in obj)) {
      throw new RefResolutionError(`#${pointer}`, `Key "${segment}" not found`);
    }
    current = obj[segment];
  }
  return current;
}

function resolveExternalRef(
  ref: string,
  basePath: string,
  fileCache: Map<string, unknown>,
): unknown {
  const hashIndex = ref.indexOf("#/");
  const filePart = hashIndex >= 0 ? ref.slice(0, hashIndex) : ref;
  const pointer = hashIndex >= 0 ? ref.slice(hashIndex + 1) : null;

  const absFilePath = resolve(basePath, filePart);
  const externalDoc = loadFile(absFilePath, fileCache);

  if (pointer) {
    return resolveJsonPointer(externalDoc, pointer);
  }
  return externalDoc;
}

/**
 * Resolves `$ref` pointers within a value tree.
 *
 * - Internal refs (`#/path/to/key`) resolve against `root`.
 * - External file refs (`./path/file.yaml` or `./file.yaml#/pointer`) resolve
 *   relative to `basePath`.
 */
export function resolveRefs<T>(
  value: T,
  options: ResolveRefsOptions = {},
): T {
  const resolved = new Set<string>();
  const fileCache = new Map<string, unknown>();
  const basePath = options.basePath ?? process.cwd();
  return deepResolve(value, value, resolved, basePath, fileCache) as T;
}

function deepResolve(
  value: unknown,
  root: unknown,
  resolved: Set<string>,
  basePath: string,
  fileCache: Map<string, unknown>,
): unknown {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) =>
      deepResolve(item, root, resolved, basePath, fileCache),
    );
  }

  const obj = value as Record<string, unknown>;

  if ("$ref" in obj && typeof obj["$ref"] === "string") {
    const ref = obj["$ref"] as string;

    if (ref.startsWith("#/")) {
      if (resolved.has(ref)) {
        throw new RefResolutionError(ref, "Circular reference detected");
      }
      resolved.add(ref);
      const pointer = ref.slice(1);
      const target = resolveJsonPointer(root, pointer);
      const result = deepResolve(target, root, resolved, basePath, fileCache);
      resolved.delete(ref);
      return result;
    }

    if (resolved.has(ref)) {
      throw new RefResolutionError(ref, "Circular reference detected");
    }
    resolved.add(ref);

    const target = resolveExternalRef(ref, basePath, fileCache);
    const hashIndex = ref.indexOf("#/");
    const externalBasePath =
      hashIndex >= 0 ? dirname(resolve(basePath, ref.slice(0, hashIndex))) : dirname(resolve(basePath, ref));

    const externalResolved = new Set<string>();
    const result = deepResolve(
      target,
      target,
      externalResolved,
      externalBasePath,
      fileCache,
    );
    resolved.delete(ref);
    return result;
  }

  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(obj)) {
    result[key] = deepResolve(val, root, resolved, basePath, fileCache);
  }
  return result;
}
